/**
 * Recently Viewed Products
 * Tracks products viewed by user and displays them on product pages
 * - Stores up to 6 products in localStorage (to display max 5 excluding current)
 * - Excludes current product from display
 * - Requires minimum 2 products (excluding current) to show section
 */

class RecentlyViewedProducts {
  constructor() {
    this.storageKey = 'piplettes-recently-viewed';
    this.maxProducts = 6;
    this.minProductsToShow = 2;
    this.currentProductHandle = null;
    this.products = [];

    this.init();
  }

  init() {
    // Check if we're on a product page
    if (window.location.pathname.includes('/products/')) {
      this.currentProductHandle = this.getProductHandleFromURL();

      if (this.currentProductHandle) {
        // Track this product view
        this.trackProduct(this.currentProductHandle);
      }
    }

    // Load recently viewed products from storage
    this.loadFromStorage();

    // Render the section if we're on a product page
    if (this.currentProductHandle) {
      this.renderSection();
    }
  }

  /**
   * Get product handle from current URL
   */
  getProductHandleFromURL() {
    const pathParts = window.location.pathname.split('/');
    const productsIndex = pathParts.indexOf('products');

    if (productsIndex !== -1 && pathParts[productsIndex + 1]) {
      return pathParts[productsIndex + 1];
    }

    return null;
  }

  /**
   * Track a product view
   */
  trackProduct(handle) {
    // Load current list
    this.loadFromStorage();

    // Remove if already exists (to move to front)
    this.products = this.products.filter(h => h !== handle);

    // Add to front
    this.products.unshift(handle);

    // Keep only max products
    this.products = this.products.slice(0, this.maxProducts);

    // Save to storage
    this.saveToStorage();
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      this.products = stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Recently Viewed: Error loading from storage', error);
      this.products = [];
    }
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.products));
    } catch (error) {
      console.error('Recently Viewed: Error saving to storage', error);
    }
  }

  /**
   * Get products to display (excluding current product)
   */
  getProductsToDisplay() {
    return this.products.filter(handle => handle !== this.currentProductHandle);
  }

  /**
   * Render the recently viewed section
   */
  async renderSection() {
    const container = document.querySelector('[data-recently-viewed-container]');

    if (!container) {
      return; // Section not on this page
    }

    const productsToDisplay = this.getProductsToDisplay();

    // Check if we have enough products to display
    if (productsToDisplay.length < this.minProductsToShow) {
      container.style.display = 'none';
      return;
    }

    // Show container
    container.style.display = 'block';

    // Get grid element
    const grid = container.querySelector('[data-recently-viewed-grid]');

    if (!grid) {
      return;
    }

    // Show loading state
    grid.innerHTML = '<div class="recently-viewed__loading"></div>';
    grid.firstElementChild.textContent = container.dataset.loadingText || '';

    try {
      // Use search template to render complete product cards with Liquid
      const fetchPromises = productsToDisplay.slice(0, 5).map(handle =>
        fetch(`${window.Shopify?.routes?.root || '/'}search?q=handle:${handle}&view=recently-viewed&type=product`)
          .then(response => response.text())
          .then(html => {
            // Extract product card from response
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const card = doc.querySelector('[data-recently-viewed-card]');
            return card ? card.outerHTML : null;
          })
          .catch(error => {
            console.error(`Error fetching product ${handle}:`, error);
            return null;
          })
      );

      const productCards = await Promise.all(fetchPromises);

      // Filter out any failed requests
      const validCards = productCards.filter(card => card !== null);

      if (validCards.length < this.minProductsToShow) {
        container.style.display = 'none';
        return;
      }

      // Render products
      grid.innerHTML = validCards.join('');

      // Initialize interactive features
      this.initializeProductCards();

    } catch (error) {
      console.error('Recently Viewed: Error fetching products', error);
      grid.innerHTML = '<p class="recently-viewed__error"></p>';
      grid.firstElementChild.textContent = container.dataset.errorText || '';
    }
  }

  /**
   * Initialize interactive features on product cards
   */
  initializeProductCards() {
    // Reinitialize wishlist buttons if system exists
    if (window.wishlistManager) {
      window.wishlistManager.updateAllButtons();
    }

    // The product-form-home web components are already defined by featured-collection-home-page.js
    // They will auto-initialize via their constructor when the HTML is inserted into the DOM
    // We just need to ensure the connectedCallback fires by forcing a reflow
    const productForms = document.querySelectorAll('.recently-viewed__grid product-form-home');
    productForms.forEach(form => {
      // Force the web component to initialize by triggering connectedCallback
      // This happens automatically when elements are inserted, but we can verify it here
      if (typeof form.initialize === 'function') {
        // Call initialize if it exists and hasn't been called yet
        form.initialize();
      }
    });

    // Trigger custom event for any other features
    const event = new CustomEvent('recently-viewed:loaded');
    document.dispatchEvent(event);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.recentlyViewedProducts = new RecentlyViewedProducts();
  });
} else {
  window.recentlyViewedProducts = new RecentlyViewedProducts();
}
